/**
 * Settings → Account — edit profile.
 *
 * Sections:
 *   1. Profile (name, sex M/F) → PATCH /me (never recomputes the natal chart).
 *   2. Birth data (date, time precision/exact, place) → PATCH /birth-data.
 *      Changing the birth DATE recomputes the natal chart + Star Persona, so we
 *      show an irreversible-action warning (ConfirmModal) before committing.
 *   3. Delete account — typed-confirm ConfirmModal → `useDeleteAccount.mutate()`.
 *
 * Changed fields are sent partially: birth fields via `usePatchBirthData`,
 * identity fields via `usePatchMePrefs`, fired together (Promise.all) on submit.
 * The exact-time field is only validated when the user is actually setting an
 * exact time — a stale EXACT-precision record never blocks unrelated edits.
 *
 * The place field uses the geocoded `PlaceAutocomplete` (same as onboarding):
 * a fresh pick sends placeName + lat/lng/tz; the prefilled value (placeId
 * 'prefill') is treated as unchanged.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PrimaryButton } from '../../components/chrome/Button';
import { MeSubpageShell } from '../../components/chrome/MeSubpageShell';
import { ConfirmModal } from '../../components/settings/ConfirmModal';
import { PlaceAutocomplete, type SelectedPlace } from '../../components/onboarding/PlaceAutocomplete';
import { useMe } from '../../hooks/useMe';
import { usePatchBirthData, type PatchBirthDataBody, type TimePrecision } from '../../hooks/usePatchBirthData';
import { usePatchMePrefs, type PatchMePrefsBody } from '../../hooks/usePatchMePrefs';
import { useDeleteAccount } from '../../hooks/useDeleteAccount';
import styles from './Account.module.css';

const PRECISION_VALUES: TimePrecision[] = ['EXACT', 'MORNING', 'AFTERNOON', 'EVENING', 'NIGHT'];

function toDateInputValue(iso: string | null): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

export function Account(): JSX.Element {
  const { t } = useTranslation(['settings', 'common']);
  const { data: me, isLoading, isError } = useMe();
  const patch = usePatchBirthData();
  const patchProfile = usePatchMePrefs();
  const deleteAccount = useDeleteAccount();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [name, setName] = useState('');
  const [sex, setSex] = useState<'MALE' | 'FEMALE' | ''>('');
  const [date, setDate] = useState('');
  const [timePrecision, setTimePrecision] = useState<TimePrecision>('MORNING');
  const [timeExact, setTimeExact] = useState('');
  const [place, setPlace] = useState<SelectedPlace | null>(null);
  const [timeError, setTimeError] = useState(false);
  const [showBirthWarning, setShowBirthWarning] = useState(false);
  const [pendingBirth, setPendingBirth] = useState<PatchBirthDataBody | null>(null);
  const [pendingProfile, setPendingProfile] = useState<PatchMePrefsBody | null>(null);

  useEffect(() => {
    if (!me) return;
    setName(me.firstName ?? '');
    setSex(me.sex ?? '');
    setDate(toDateInputValue(me.birthDate));
    setTimePrecision(((me.birthTimePrecision as TimePrecision | null) ?? 'MORNING'));
    setTimeExact(me.birthTimeExact ?? '');
    // Prefill the geocoded place from stored coords (placeId 'prefill' marks it
    // as unchanged — only a fresh pick has a real placeId).
    if (me.birthPlaceName && me.birthLat != null && me.birthLng != null && me.birthTimezone) {
      setPlace({
        placeId: 'prefill',
        placeName: me.birthPlaceName,
        state: null,
        country: null,
        countryCode: null,
        lat: me.birthLat,
        lng: me.birthLng,
        tz: me.birthTimezone,
      });
    } else {
      setPlace(null);
    }
  }, [me]);

  const runMutations = (birthBody: PatchBirthDataBody, profileBody: PatchMePrefsBody): void => {
    const ops: Array<Promise<unknown>> = [];
    if (Object.keys(birthBody).length > 0) ops.push(patch.mutateAsync(birthBody));
    if (Object.keys(profileBody).length > 0) ops.push(patchProfile.mutateAsync(profileBody));
    if (ops.length === 0) return;
    void Promise.all(ops).catch(() => {
      /* per-mutation isError surfaces the message; nothing to do here */
    });
  };

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!me) return;
    setTimeError(false);

    // Birth-data fields → PATCH /birth-data (changing date/time/place recomputes the natal chart).
    const birthBody: PatchBirthDataBody = {};
    const origDate = toDateInputValue(me.birthDate);
    if (date && date !== origDate) birthBody.date = date;
    const precisionChanged = timePrecision !== (me.birthTimePrecision ?? 'MORNING');
    if (precisionChanged) birthBody.timePrecision = timePrecision;
    const timeChanged = timeExact !== (me.birthTimeExact ?? '');
    if (timePrecision === 'EXACT' && (precisionChanged || timeChanged)) {
      // Sending EXACT precision requires a valid HH:MM. Validate only when the user
      // is actually setting an exact time — never block unrelated edits (name, etc.).
      if (!/^\d{2}:\d{2}$/.test(timeExact)) {
        setTimeError(true);
        return;
      }
      birthBody.timeExact = timeExact;
    }
    // A real place change is a fresh geocoded pick (placeId !== 'prefill') —
    // send the full coords so the chart recomputes against the new location.
    if (place && place.placeId !== 'prefill') {
      birthBody.placeName = place.placeName;
      birthBody.lat = place.lat;
      birthBody.lng = place.lng;
      birthBody.tz = place.tz;
    }

    // Identity fields → PATCH /me (never touches the natal chart).
    const profileBody: PatchMePrefsBody = {};
    const trimmedName = name.trim();
    if (trimmedName && trimmedName !== (me.firstName ?? '')) profileBody.name = trimmedName;
    const nextSex = sex === '' ? null : sex;
    if (nextSex !== (me.sex ?? null)) profileBody.sex = nextSex;

    if (Object.keys(birthBody).length === 0 && Object.keys(profileBody).length === 0) return;

    // Changing the birth DATE wipes & recomputes the natal chart + Star Persona —
    // warn before committing (irreversible).
    if (birthBody.date !== undefined) {
      setPendingBirth(birthBody);
      setPendingProfile(profileBody);
      setShowBirthWarning(true);
      return;
    }
    runMutations(birthBody, profileBody);
  };

  const saving = patch.isPending || patchProfile.isPending;

  return (
    <MeSubpageShell title={t('account.title', { ns: 'settings' })} data-testid="settings-account-page">
      {isLoading && <p data-testid="account-loading">{t('states.loading', { ns: 'common' })}</p>}
      {isError && (
        <p role="alert" data-testid="account-error">{t('account.loadError', { ns: 'settings' })}</p>
      )}
      {!isLoading && !isError && me && (
        <>
          <form className={styles.section} onSubmit={onSubmit} noValidate data-testid="account-birth-form">
            <p className={styles.sectionLabel}>{t('account.profile.title', { ns: 'settings' })}</p>

            <label className={styles.field}>
              <span className={styles.label}>{t('account.profile.name', { ns: 'settings' })}</span>
              <input
                type="text"
                className={styles.input}
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="account-profile-name"
              />
            </label>

            <label className={styles.field}>
              <span className={styles.label}>{t('account.profile.sex', { ns: 'settings' })}</span>
              <select
                className={styles.select}
                value={sex}
                onChange={(e) => setSex(e.target.value as 'MALE' | 'FEMALE' | '')}
                data-testid="account-profile-sex"
              >
                <option value="" disabled>{t('account.profile.sexOptions.UNSET', { ns: 'settings' })}</option>
                <option value="MALE">{t('account.profile.sexOptions.MALE', { ns: 'settings' })}</option>
                <option value="FEMALE">{t('account.profile.sexOptions.FEMALE', { ns: 'settings' })}</option>
              </select>
            </label>

            <p className={styles.sectionLabel}>{t('account.birthData.title', { ns: 'settings' })}</p>

            <label className={styles.field}>
              <span className={styles.label}>{t('account.birthData.birthDate', { ns: 'settings' })}</span>
              <input
                type="date"
                className={styles.input}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="account-birth-date"
              />
            </label>

            <label className={styles.field}>
              <span className={styles.label}>{t('account.birthData.timePrecision', { ns: 'settings' })}</span>
              <select
                className={styles.select}
                value={timePrecision}
                onChange={(e) => setTimePrecision(e.target.value as TimePrecision)}
                data-testid="account-birth-precision"
              >
                {PRECISION_VALUES.map((value) => (
                  <option key={value} value={value}>
                    {t(`account.birthData.precision.${value}`, { ns: 'settings' })}
                  </option>
                ))}
              </select>
            </label>

            {timePrecision === 'EXACT' && (
              <label className={styles.field}>
                <span className={styles.label}>{t('account.birthData.timeExact', { ns: 'settings' })}</span>
                <input
                  type="time"
                  className={styles.input}
                  value={timeExact}
                  onChange={(e) => setTimeExact(e.target.value)}
                  data-testid="account-birth-time"
                />
              </label>
            )}

            <div className={styles.field}>
              <span className={styles.label}>{t('account.birthData.birthPlace', { ns: 'settings' })}</span>
              <PlaceAutocomplete
                value={place}
                onChange={setPlace}
                placeholder={t('account.birthData.birthPlace', { ns: 'settings' })}
                ariaLabel={t('account.birthData.birthPlace', { ns: 'settings' })}
              />
            </div>

            {timeError && (
              <p role="alert" data-testid="account-time-error">
                {t('account.birthData.timeInvalid', { ns: 'settings' })}
              </p>
            )}

            <PrimaryButton
              type="submit"
              disabled={saving}
              data-testid="account-birth-save"
            >
              {saving
                ? t('account.birthData.saving', { ns: 'settings' })
                : t('account.birthData.save', { ns: 'settings' })}
            </PrimaryButton>

            {(patch.isError || patchProfile.isError) && (
              <p role="alert" data-testid="account-save-error">
                {t('account.saveError', { ns: 'settings' })}
              </p>
            )}
          </form>

          <section className={styles.section} aria-label={t('account.delete.label', { ns: 'settings' })}>
            <p className={styles.sectionLabel}>{t('account.dangerZone', { ns: 'settings' })}</p>
            <div className={styles.deleteRow}>
              <div className={styles.deleteText}>
                <p className={styles.deleteLabel}>{t('account.delete.label', { ns: 'settings' })}</p>
                <p className={styles.deleteDescription}>
                  {t('account.delete.description', { ns: 'settings' })}
                </p>
              </div>
              <button
                type="button"
                className={styles.deleteBtn}
                onClick={() => setShowDeleteConfirm(true)}
                data-testid="account-delete-btn"
              >
                {t('account.delete.button', { ns: 'settings' })}
              </button>
            </div>
          </section>
        </>
      )}

      {showBirthWarning && (
        <ConfirmModal
          title={t('account.birthData.warnTitle', { ns: 'settings' })}
          body={t('account.birthData.warnBody', { ns: 'settings' })}
          confirmLabel={t('account.birthData.warnConfirm', { ns: 'settings' })}
          cancelLabel={t('actions.cancel', { ns: 'common' })}
          variant="danger"
          onConfirm={() => {
            setShowBirthWarning(false);
            if (pendingBirth) runMutations(pendingBirth, pendingProfile ?? {});
            setPendingBirth(null);
            setPendingProfile(null);
          }}
          onCancel={() => {
            setShowBirthWarning(false);
            setPendingBirth(null);
            setPendingProfile(null);
          }}
          data-testid="account-birth-warning"
        />
      )}

      {showDeleteConfirm && (
        <ConfirmModal
          title={t('account.delete.confirmTitle', { ns: 'settings' })}
          body={t('account.delete.confirmBody', { ns: 'settings' })}
          confirmLabel={t('account.delete.confirmAction', { ns: 'settings' })}
          cancelLabel={t('actions.cancel', { ns: 'common' })}
          variant="danger"
          requireTypedConfirm="DELETE"
          onConfirm={() => {
            deleteAccount.mutate();
            setShowDeleteConfirm(false);
          }}
          onCancel={() => setShowDeleteConfirm(false)}
          data-testid="account-delete-confirm"
        />
      )}
    </MeSubpageShell>
  );
}
